import { Constants } from '../constants';
import { BUNDLED_ARMORY_SHARE_CONTRACT_VERSION } from '../constants/constants';
import { Argonaut, ArmoryItem, ArmoryLoadout, LoadoutStats } from '../types';
import type { SharedArmoryImportPreview } from './armory-storage';
import { deepCopy } from './data-replication';
import { isBundledArmoryItem } from './armory-storage';
import { sanitizeLoadoutStats, sanitizeLoadoutStatsWithRequirements } from './sanitization';

export const LOADOUT_SHARE_ENVELOPE_VERSION = 2;
const SHARE_HASH_PARAM = 's';
const SHARED_DICE_TOKEN_BY_COLOR = {
    Red: 'R',
    Black: 'B',
    White: 'W',
    Mortal: 'M',
} as const;
const SHARED_DICE_COLOR_BY_TOKEN = {
    R: 'Red',
    B: 'Black',
    W: 'White',
    M: 'Mortal',
} as const;

export type SharedLoadoutInputSource = 'manual' | 'armory';
export type SharedLoadoutImportMode = 'add' | 'replace';
export type SharedLoadoutImportTarget = 'manual' | 'armory';

export interface SharedLoadoutMeta {
    sharedAt?: string;
    sourceLoadoutName?: string;
}

export interface SharedBundledArmoryRef {
    kind: 'bundled-ref';
    id: string;
    name?: string;
}

export interface SharedCustomArmoryItem {
    kind: 'custom-item';
    item: ArmoryItem;
}

export type SharedArmoryItemSelection = SharedBundledArmoryRef | SharedCustomArmoryItem;

export interface SharedArmoryRecipe {
    bundledDatasetVersion: string;
    argonaut?: Argonaut;
    titan?: SharedArmoryItemSelection;
    kratosTable?: SharedArmoryItemSelection;
    weapon?: SharedArmoryItemSelection;
    gear?: SharedArmoryItemSelection[];
    mnemos?: SharedArmoryItemSelection[];
}

export interface SharedLoadoutEnvelopeV1 {
    version: 2;
    scope: 'loadout';
    importTarget: 'manual';
    inputSource: SharedLoadoutInputSource;
    payload: {
        snapshot: LoadoutStats;
        armoryRecipe?: SharedArmoryRecipe;
    };
    meta?: SharedLoadoutMeta;
}

export type SharedLoadoutEnvelope = SharedLoadoutEnvelopeV1;

export interface SharedLoadoutImportCandidate {
    title: string;
    inputSource: SharedLoadoutInputSource;
    importTarget: 'manual';
    snapshot: LoadoutStats;
    armoryRecipe?: SharedArmoryRecipe;
    meta?: SharedLoadoutMeta;
}

export interface SharedLoadoutImportRequest {
    requestId: string;
    mode: SharedLoadoutImportMode;
    importTarget: SharedLoadoutImportTarget;
    candidate: SharedLoadoutImportCandidate;
    missingSpoilerTags: string[];
    armoryImportPreview?: SharedArmoryImportPreview;
}

export interface SavedLoadoutRecord {
    id: string;
    title: string;
    inputSource: SharedLoadoutInputSource;
    snapshot: LoadoutStats;
    armoryRecipe?: SharedArmoryRecipe;
    meta?: SharedLoadoutMeta;
    isFavorite: boolean;
    createdAt: string;
    updatedAt: string;
}

interface CompactSharedLoadoutEnvelopeV2 {
    v: 2;
    i: 'm' | 'a';
    s: CompactLoadoutStats;
    a?: CompactArmoryRecipe;
    n?: string;
}

type CompactLoadoutStats = Partial<{
    n: string;
    a: number;
    r: number;
    h: number;
    t: CompactKratosTokens;
    m: number;
    o: 1;
    ph: string;
    po: string[];
    b: number;
    d: string;
    p: number;
    c: number;
}>;

type CompactKratosTokens = Partial<{
    b: number;
    k: number;
    f: number;
    h: number;
    o: number;
    c: number;
    r: number;
}>;

type CompactArgonautTokens = Partial<{
    a: number;
    d: number;
    b: number;
    m: number;
    p: number;
}>;

type CompactArgonaut = CompactLoadoutStats &
    Partial<{
        rg: number;
        ft: number;
        dg: number;
        at: CompactArgonautTokens;
    }>;

type CompactArmorySelection = string | { c: ArmoryItem };

type CompactArmoryRecipe = Partial<{
    v: string;
    g: CompactArgonaut;
    t: CompactArmorySelection;
    k: CompactArmorySelection;
    w: CompactArmorySelection;
    e: CompactArmorySelection[];
    m: CompactArmorySelection[];
}>;

export function buildManualLoadoutShareEnvelope(
    loadout: LoadoutStats,
    meta: SharedLoadoutMeta = {},
): SharedLoadoutEnvelope {
    const snapshot = sanitizeSnapshot(loadout);

    return {
        version: LOADOUT_SHARE_ENVELOPE_VERSION,
        scope: 'loadout',
        importTarget: 'manual',
        inputSource: 'manual',
        payload: {
            snapshot,
        },
        meta: buildMeta(snapshot.name, meta),
    };
}

export function buildArmoryLoadoutShareEnvelope(
    loadout: ArmoryLoadout,
    snapshot: LoadoutStats,
    meta: SharedLoadoutMeta = {},
): SharedLoadoutEnvelope {
    const sanitizedSnapshot = sanitizeSnapshot(snapshot);

    return {
        version: LOADOUT_SHARE_ENVELOPE_VERSION,
        scope: 'loadout',
        importTarget: 'manual',
        inputSource: 'armory',
        payload: {
            snapshot: sanitizedSnapshot,
            armoryRecipe: buildArmoryRecipe(loadout),
        },
        meta: buildMeta(sanitizedSnapshot.name, meta),
    };
}

export function encodeSharedLoadoutEnvelope(envelope: SharedLoadoutEnvelope): string {
    const normalized = normalizeEnvelope(envelope);
    if (!normalized) {
        throw new Error('Cannot encode an invalid loadout share payload.');
    }

    return encodeBase64Url(JSON.stringify(toCompactEnvelope(normalized)));
}

export function decodeSharedLoadoutEnvelope(encoded: string): SharedLoadoutEnvelope | null {
    if (!encoded?.trim()) {
        return null;
    }

    try {
        const decoded = decodeBase64Url(encoded);
        return fromCompactEnvelope(JSON.parse(decoded));
    } catch {
        return null;
    }
}

export function buildSharedLoadoutUrl(currentUrl: string, envelope: SharedLoadoutEnvelope): string {
    const url = new URL(currentUrl);
    const params = new URLSearchParams(url.hash.replace(/^#/, ''));
    params.set(SHARE_HASH_PARAM, encodeSharedLoadoutEnvelope(envelope));
    url.hash = params.toString();
    return url.toString();
}

export function clearSharedLoadoutHash(currentUrl: string): string {
    const url = new URL(currentUrl);
    const params = new URLSearchParams(url.hash.replace(/^#/, ''));
    params.delete(SHARE_HASH_PARAM);
    params.delete('share');
    const nextHash = params.toString();
    url.hash = nextHash;
    return url.toString();
}

export function readSharedLoadoutFromHash(hash: string): SharedLoadoutImportCandidate | null {
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const encoded = params.get(SHARE_HASH_PARAM) ?? params.get('share');
    if (!encoded) {
        return null;
    }

    const envelope = decodeSharedLoadoutEnvelope(encoded);
    if (!envelope) {
        return null;
    }

    return toSharedLoadoutImportCandidate(envelope);
}

export function readSharedLoadoutFromText(input: string): SharedLoadoutImportCandidate | null {
    const trimmed = input?.trim();
    if (!trimmed) {
        return null;
    }

    const hashCandidate = readSharedLoadoutFromHash(trimmed.startsWith('#') ? trimmed : `#${trimmed}`);
    if (hashCandidate) {
        return hashCandidate;
    }

    try {
        const url = new URL(trimmed);
        const fromUrlHash = readSharedLoadoutFromHash(url.hash);
        if (fromUrlHash) {
            return fromUrlHash;
        }
    } catch {
        // Fall through to raw payload decoding.
    }

    const envelope = decodeSharedLoadoutEnvelope(trimmed);
    return envelope ? toSharedLoadoutImportCandidate(envelope) : null;
}

export function toSharedLoadoutImportCandidate(envelope: SharedLoadoutEnvelope): SharedLoadoutImportCandidate {
    return {
        title: envelope.meta?.sourceLoadoutName?.trim() || envelope.payload.snapshot.name?.trim() || 'Shared Loadout',
        inputSource: envelope.inputSource,
        importTarget: envelope.importTarget,
        snapshot: envelope.payload.snapshot,
        armoryRecipe: envelope.payload.armoryRecipe,
        meta: envelope.meta,
    };
}

export function buildSharedLoadoutEnvelopeFromCandidate(candidate: SharedLoadoutImportCandidate): SharedLoadoutEnvelope {
    return normalizeEnvelope({
        version: LOADOUT_SHARE_ENVELOPE_VERSION,
        scope: 'loadout',
        importTarget: 'manual',
        inputSource: candidate.inputSource,
        payload: {
            snapshot: candidate.snapshot,
            ...(candidate.armoryRecipe ? { armoryRecipe: candidate.armoryRecipe } : {}),
        },
        ...(candidate.meta ? { meta: candidate.meta } : {}),
    })!;
}

export function buildSavedLoadoutRecord(
    candidate: SharedLoadoutImportCandidate,
    options: Partial<Pick<SavedLoadoutRecord, 'createdAt' | 'updatedAt' | 'isFavorite'>> = {},
): SavedLoadoutRecord {
    const id = buildSavedLoadoutRecordId(candidate);
    const timestamp = options.updatedAt ?? new Date().toISOString();

    return {
        id,
        title: candidate.title,
        inputSource: candidate.inputSource,
        snapshot: sanitizeSnapshot(candidate.snapshot),
        ...(candidate.armoryRecipe ? { armoryRecipe: normalizeArmoryRecipe(candidate.armoryRecipe) } : {}),
        ...(candidate.meta ? { meta: normalizeMeta(candidate.meta, candidate.title) } : {}),
        isFavorite: options.isFavorite ?? true,
        createdAt: options.createdAt ?? timestamp,
        updatedAt: timestamp,
    };
}

export function buildSavedLoadoutRecordId(candidate: SharedLoadoutImportCandidate): string {
    const normalizedTitle = candidate.title?.trim() || candidate.snapshot.name?.trim() || 'Saved Loadout';
    const normalizedMeta = candidate.meta?.sourceLoadoutName?.trim()
        ? {
              sourceLoadoutName: candidate.meta.sourceLoadoutName.trim(),
          }
        : {
              sourceLoadoutName: normalizedTitle,
          };

    const identityPayload =
        candidate.inputSource === 'armory' && candidate.armoryRecipe
            ? {
                  inputSource: 'armory' as const,
                  armoryRecipe: toCompactArmoryRecipe(normalizeArmoryRecipeForIdentity(candidate.armoryRecipe)!),
                  meta: normalizedMeta,
              }
            : {
                  inputSource: candidate.inputSource,
                  snapshot: sanitizeSnapshot(candidate.snapshot),
                  meta: normalizedMeta,
              };

    return `saved:${encodeBase64Url(
        JSON.stringify({
            version: LOADOUT_SHARE_ENVELOPE_VERSION,
            scope: 'saved-loadout',
            payload: identityPayload,
        }),
    )}`;
}

export function toSharedLoadoutImportCandidateFromSavedRecord(record: SavedLoadoutRecord): SharedLoadoutImportCandidate {
    return {
        title: record.title,
        inputSource: record.inputSource,
        importTarget: 'manual',
        snapshot: sanitizeSnapshot(record.snapshot),
        ...(record.armoryRecipe ? { armoryRecipe: normalizeArmoryRecipe(record.armoryRecipe) } : {}),
        ...(record.meta ? { meta: normalizeMeta(record.meta, record.title) } : {}),
    };
}

export function normalizeSavedLoadoutRecord(value: unknown): SavedLoadoutRecord | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const record = value as Partial<SavedLoadoutRecord>;
    if (!record.snapshot || typeof record.snapshot !== 'object') {
        return null;
    }

    const normalizedArmoryRecipe = normalizeArmoryRecipe(record.armoryRecipe);
    const normalizedMeta = normalizeMeta(record.meta, record.title?.trim() || 'Saved Loadout');
    const candidate = toSharedLoadoutImportCandidate(
        buildSharedLoadoutEnvelopeFromCandidate({
            title: record.title?.trim() || 'Saved Loadout',
            inputSource: record.inputSource === 'armory' ? 'armory' : 'manual',
            importTarget: 'manual',
            snapshot: sanitizeSnapshot(record.snapshot as LoadoutStats),
            ...(normalizedArmoryRecipe ? { armoryRecipe: normalizedArmoryRecipe } : {}),
            ...(normalizedMeta ? { meta: normalizedMeta } : {}),
        }),
    );
    const normalized = buildSavedLoadoutRecord(candidate, {
        createdAt: record.createdAt?.trim() || undefined,
        updatedAt: record.updatedAt?.trim() || undefined,
        isFavorite: record.isFavorite ?? true,
    });

    return normalized;
}

export function getRequiredSpoilerTagsForLoadoutSnapshot(snapshot: LoadoutStats): string[] {
    const requiredTags = new Set<string>();

    if ((snapshot.autoTokens.black ?? 0) > 0 || (snapshot.autoTokens.fire ?? 0) > 0) {
        requiredTags.add(Constants.SpoilerTag.CYCLE_II);
    }

    if ((snapshot.autoTokens.hope ?? 0) > 0) {
        requiredTags.add(Constants.SpoilerTag.CYCLE_III);
    }

    if ((snapshot.autoTokens.closing ?? 0) > 0) {
        requiredTags.add(Constants.SpoilerTag.CYCLE_IV);
    }

    return Array.from(requiredTags).sort((a, b) => a.localeCompare(b));
}

function buildMeta(fallbackTitle: string, meta: SharedLoadoutMeta): SharedLoadoutMeta {
    const sourceLoadoutName = meta.sourceLoadoutName?.trim() || fallbackTitle.trim() || undefined;
    const sharedAt = meta.sharedAt?.trim() || undefined;

    if (!sourceLoadoutName && !sharedAt) {
        return {};
    }

    return {
        ...(sharedAt ? { sharedAt } : {}),
        ...(sourceLoadoutName ? { sourceLoadoutName } : {}),
    };
}

function buildArmoryRecipe(loadout: ArmoryLoadout): SharedArmoryRecipe {
    return {
        bundledDatasetVersion: BUNDLED_ARMORY_SHARE_CONTRACT_VERSION,
        argonaut: sanitizeSharedArgonaut(loadout.argonaut),
        titan: toSharedArmorySelection(loadout.titan),
        kratosTable: toSharedArmorySelection(loadout.kratosTable),
        weapon: toSharedArmorySelection(loadout.weapon),
        gear: toSharedArmorySelectionArray(loadout.gear),
        mnemos: toSharedArmorySelectionArray(loadout.mnemos),
    };
}

function toSharedArmorySelection(item?: ArmoryItem | null): SharedArmoryItemSelection | undefined {
    if (!item) {
        return undefined;
    }

    if (isBundledArmoryItem(item)) {
        const id = item.id?.trim();
        if (!id) {
            throw new Error(`Bundled armory item "${item.name}" is missing a stable id.`);
        }

        return {
            kind: 'bundled-ref',
            id,
            name: item.name.trim(),
        };
    }

    return {
        kind: 'custom-item',
        item: sanitizeCustomArmoryItem(item),
    };
}

function toSharedArmorySelectionArray(items?: ArmoryItem[] | null): SharedArmoryItemSelection[] | undefined {
    if (!Array.isArray(items) || items.length === 0) {
        return undefined;
    }

    const selections = items.map((item) => toSharedArmorySelection(item)).filter(Boolean) as SharedArmoryItemSelection[];
    return selections.length > 0 ? selections : undefined;
}

function toCompactEnvelope(envelope: SharedLoadoutEnvelope): CompactSharedLoadoutEnvelopeV2 {
    const sourceLoadoutName = envelope.meta?.sourceLoadoutName?.trim();
    const snapshotName = envelope.payload.snapshot.name?.trim();

    return {
        v: LOADOUT_SHARE_ENVELOPE_VERSION,
        i: envelope.inputSource === 'armory' ? 'a' : 'm',
        s: toCompactLoadoutStats(envelope.payload.snapshot),
        ...(envelope.payload.armoryRecipe ? { a: toCompactArmoryRecipe(envelope.payload.armoryRecipe) } : {}),
        ...(sourceLoadoutName && sourceLoadoutName !== snapshotName ? { n: sourceLoadoutName } : {}),
    };
}

function fromCompactEnvelope(value: unknown): SharedLoadoutEnvelope | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const envelope = value as Partial<CompactSharedLoadoutEnvelopeV2>;
    if (envelope.v !== LOADOUT_SHARE_ENVELOPE_VERSION || (envelope.i !== 'm' && envelope.i !== 'a')) {
        return null;
    }
    if (!envelope.s || typeof envelope.s !== 'object') {
        return null;
    }

    const snapshot = fromCompactLoadoutStats(envelope.s);
    if (!snapshot) {
        return null;
    }

    return normalizeEnvelope({
        version: LOADOUT_SHARE_ENVELOPE_VERSION,
        scope: 'loadout',
        importTarget: 'manual',
        inputSource: envelope.i === 'a' ? 'armory' : 'manual',
        payload: {
            snapshot,
            ...(envelope.a ? { armoryRecipe: fromCompactArmoryRecipe(envelope.a) } : {}),
        },
        ...(envelope.n?.trim() ? { meta: { sourceLoadoutName: envelope.n.trim() } } : {}),
    });
}

function toCompactLoadoutStats(loadout: LoadoutStats): CompactLoadoutStats {
    const sanitized = sanitizeSnapshot(loadout);
    const compact: CompactLoadoutStats = {};

    if (sanitized.name.trim()) compact.n = sanitized.name.trim();
    if (sanitized.attackDice !== Constants.DEFAULT_LOADOUT_STATS.attackDice) compact.a = sanitized.attackDice;
    if (sanitized.attackReRolls !== Constants.DEFAULT_LOADOUT_STATS.attackReRolls) compact.r = sanitized.attackReRolls;
    if (sanitized.autoHits !== Constants.DEFAULT_LOADOUT_STATS.autoHits) compact.h = sanitized.autoHits;

    const compactAutoTokens = toCompactKratosTokens(sanitized.autoTokens);
    if (compactAutoTokens) compact.t = compactAutoTokens;

    if (sanitized.maxHits !== Constants.DEFAULT_LOADOUT_STATS.maxHits) compact.m = sanitized.maxHits;
    if (sanitized.overridePowerDice) compact.o = 1;

    const perHitPowerDice = encodeDiceList(sanitized.perHitPowerDice);
    if (perHitPowerDice) compact.ph = perHitPowerDice;

    const perHitPowerDiceOverride = trimTrailingEmptyDiceRows(sanitized.perHitPowerDiceOverride).map((dice) => encodeDiceList(dice));
    if (perHitPowerDiceOverride.length > 0) compact.po = perHitPowerDiceOverride;

    if (sanitized.powerBonus !== Constants.DEFAULT_LOADOUT_STATS.powerBonus) compact.b = sanitized.powerBonus;

    const powerDice = encodeDiceList(sanitized.powerDice);
    if (powerDice) compact.d = powerDice;

    if (sanitized.powerReRolls !== Constants.DEFAULT_LOADOUT_STATS.powerReRolls) compact.p = sanitized.powerReRolls;
    if (sanitized.precisionBonus !== Constants.DEFAULT_LOADOUT_STATS.precisionBonus) compact.c = sanitized.precisionBonus;

    return compact;
}

function fromCompactLoadoutStats(compact: CompactLoadoutStats | undefined, base: LoadoutStats = Constants.DEFAULT_LOADOUT_STATS): LoadoutStats | null {
    if (compact !== undefined && (!compact || typeof compact !== 'object')) {
        return null;
    }

    const maxHits = compact?.m ?? base.maxHits;
    const perHitPowerDiceOverride = compact?.po ? compact.po.map((dice) => decodeDiceList(dice)) : deepCopy(base.perHitPowerDiceOverride);
    while (perHitPowerDiceOverride.length < maxHits) {
        perHitPowerDiceOverride.push([]);
    }
    if (perHitPowerDiceOverride.length > maxHits) {
        perHitPowerDiceOverride.length = maxHits;
    }

    return sanitizeLoadoutStats({
        ...deepCopy(base),
        name: compact?.n?.trim() ?? base.name,
        attackDice: compact?.a ?? base.attackDice,
        attackReRolls: compact?.r ?? base.attackReRolls,
        autoHits: compact?.h ?? base.autoHits,
        autoTokens: fromCompactKratosTokens(compact?.t, base.autoTokens),
        maxHits,
        overridePowerDice: compact?.o === 1 ? true : base.overridePowerDice,
        perHitPowerDice: compact?.ph ? decodeDiceList(compact.ph) : deepCopy(base.perHitPowerDice),
        perHitPowerDiceOverride,
        powerBonus: compact?.b ?? base.powerBonus,
        powerDice: compact?.d ? decodeDiceList(compact.d) : deepCopy(base.powerDice),
        powerReRolls: compact?.p ?? base.powerReRolls,
        precisionBonus: compact?.c ?? base.precisionBonus,
    });
}

function toCompactKratosTokens(tokens: LoadoutStats['autoTokens']): CompactKratosTokens | undefined {
    const compact: CompactKratosTokens = {};

    if ((tokens.break ?? 0) !== 0) compact.b = tokens.break;
    if ((tokens.black ?? 0) !== 0) compact.k = tokens.black;
    if ((tokens.fire ?? 0) !== 0) compact.f = tokens.fire;
    if ((tokens.hope ?? 0) !== 0) compact.h = tokens.hope;
    if ((tokens.opening ?? 0) !== 0) compact.o = tokens.opening;
    if ((tokens.closing ?? 0) !== 0) compact.c = tokens.closing;
    if ((tokens.rouse ?? 0) !== 0) compact.r = tokens.rouse;

    return Object.keys(compact).length > 0 ? compact : undefined;
}

function fromCompactKratosTokens(compact: CompactKratosTokens | undefined, base: LoadoutStats['autoTokens']): LoadoutStats['autoTokens'] {
    if (!compact || typeof compact !== 'object') {
        return deepCopy(base);
    }

    return {
        ...deepCopy(base),
        break: compact.b ?? base.break,
        black: compact.k ?? base.black,
        fire: compact.f ?? base.fire,
        hope: compact.h ?? base.hope,
        opening: compact.o ?? base.opening,
        closing: compact.c ?? base.closing,
        rouse: compact.r ?? base.rouse,
    };
}

function toCompactArmoryRecipe(recipe: SharedArmoryRecipe): CompactArmoryRecipe {
    const compact: CompactArmoryRecipe = {};
    const compactArgonaut = recipe.argonaut ? toCompactArgonaut(recipe.argonaut) : undefined;

    compact.v = recipe.bundledDatasetVersion?.trim() || BUNDLED_ARMORY_SHARE_CONTRACT_VERSION;
    if (compactArgonaut) compact.g = compactArgonaut;
    if (recipe.titan) compact.t = toCompactArmorySelection(recipe.titan);
    if (recipe.kratosTable) compact.k = toCompactArmorySelection(recipe.kratosTable);
    if (recipe.weapon) compact.w = toCompactArmorySelection(recipe.weapon);
    if (recipe.gear?.length) compact.e = recipe.gear.map((selection) => toCompactArmorySelection(selection));
    if (recipe.mnemos?.length) compact.m = recipe.mnemos.map((selection) => toCompactArmorySelection(selection));

    return compact;
}

function fromCompactArmoryRecipe(compact: CompactArmoryRecipe | undefined): SharedArmoryRecipe | undefined {
    if (!compact || typeof compact !== 'object') {
        return undefined;
    }

    const titan = fromCompactArmorySelection(compact.t);
    const kratosTable = fromCompactArmorySelection(compact.k);
    const weapon = fromCompactArmorySelection(compact.w);
    const gear = fromCompactArmorySelectionList(compact.e);
    const mnemos = fromCompactArmorySelectionList(compact.m);
    const hasSelections = Boolean(titan || kratosTable || weapon || gear || mnemos);
    const argonaut = compact.g ? fromCompactArgonaut(compact.g) : hasSelections ? createDefaultSharedArgonaut() : undefined;

    if (!argonaut && !hasSelections) {
        return undefined;
    }

    return {
        bundledDatasetVersion: compact.v?.trim() || BUNDLED_ARMORY_SHARE_CONTRACT_VERSION,
        ...(argonaut ? { argonaut } : {}),
        ...(titan ? { titan } : {}),
        ...(kratosTable ? { kratosTable } : {}),
        ...(weapon ? { weapon } : {}),
        ...(gear ? { gear } : {}),
        ...(mnemos ? { mnemos } : {}),
    };
}

function toCompactArgonaut(argonaut: Argonaut): CompactArgonaut | undefined {
    const sanitized = sanitizeSharedArgonaut(argonaut);
    const compact: CompactArgonaut = {
        ...toCompactLoadoutStats(sanitized),
    };
    const defaultArgonaut = createDefaultSharedArgonaut();

    if (compact.n === defaultArgonaut.name) {
        delete compact.n;
    }

    if (sanitized.rage !== defaultArgonaut.rage) compact.rg = sanitized.rage;
    if (sanitized.fate !== defaultArgonaut.fate) compact.ft = sanitized.fate;
    if (sanitized.danger !== defaultArgonaut.danger) compact.dg = sanitized.danger;

    const compactTokens: CompactArgonautTokens = {};
    if (sanitized.ambrosia !== defaultArgonaut.ambrosia) compactTokens.a = sanitized.ambrosia;
    if (sanitized.despair !== defaultArgonaut.despair) compactTokens.d = sanitized.despair;
    if (sanitized.bleed !== defaultArgonaut.bleed) compactTokens.b = sanitized.bleed;
    if (sanitized.midas !== defaultArgonaut.midas) compactTokens.m = sanitized.midas;
    if (sanitized.pain !== defaultArgonaut.pain) compactTokens.p = sanitized.pain;
    if (Object.keys(compactTokens).length > 0) compact.at = compactTokens;

    return Object.keys(compact).length > 0 ? compact : undefined;
}

function fromCompactArgonaut(compact: CompactArgonaut | undefined): Argonaut {
    const base = createDefaultSharedArgonaut();
    const expanded = fromCompactLoadoutStats(compact, base);

    return sanitizeSharedArgonaut({
        ...expanded,
        name: compact?.n?.trim() || base.name,
        rage: compact?.rg ?? base.rage,
        fate: compact?.ft ?? base.fate,
        danger: compact?.dg ?? base.danger,
        ambrosia: compact?.at?.a ?? base.ambrosia,
        despair: compact?.at?.d ?? base.despair,
        bleed: compact?.at?.b ?? base.bleed,
        midas: compact?.at?.m ?? base.midas,
        pain: compact?.at?.p ?? base.pain,
    } as Argonaut);
}

function createDefaultSharedArgonaut(): Argonaut {
    return sanitizeSharedArgonaut({
        name: 'Argonaut',
        rage: 0,
        fate: 0,
        danger: 0,
    } as Argonaut);
}

function toCompactArmorySelection(selection: SharedArmoryItemSelection): CompactArmorySelection {
    if (selection.kind === 'bundled-ref') {
        return selection.id;
    }

    return {
        c: sanitizeCustomArmoryItem(selection.item),
    };
}

function fromCompactArmorySelection(selection: CompactArmorySelection | undefined): SharedArmoryItemSelection | undefined {
    if (!selection) {
        return undefined;
    }

    if (typeof selection === 'string') {
        const id = selection.trim();
        return id
            ? {
                  kind: 'bundled-ref',
                  id,
              }
            : undefined;
    }

    if (typeof selection === 'object' && 'c' in selection) {
        try {
            return {
                kind: 'custom-item',
                item: sanitizeCustomArmoryItem(selection.c),
            };
        } catch {
            return undefined;
        }
    }

    return undefined;
}

function fromCompactArmorySelectionList(selections: CompactArmorySelection[] | undefined): SharedArmoryItemSelection[] | undefined {
    if (!Array.isArray(selections) || selections.length === 0) {
        return undefined;
    }

    const normalized = selections.map((selection) => fromCompactArmorySelection(selection)).filter(Boolean) as SharedArmoryItemSelection[];
    return normalized.length > 0 ? normalized : undefined;
}

function encodeDiceList(dice: string[]): string {
    return dice
        .map((die) => SHARED_DICE_TOKEN_BY_COLOR[die as keyof typeof SHARED_DICE_TOKEN_BY_COLOR] ?? '')
        .join('');
}

function decodeDiceList(encoded: string): LoadoutStats['powerDice'] {
    return Array.from(encoded)
        .map((token) => SHARED_DICE_COLOR_BY_TOKEN[token as keyof typeof SHARED_DICE_COLOR_BY_TOKEN])
        .filter(Boolean) as LoadoutStats['powerDice'];
}

function trimTrailingEmptyDiceRows(diceRows: LoadoutStats['perHitPowerDiceOverride']): LoadoutStats['perHitPowerDiceOverride'] {
    const normalized = deepCopy(diceRows);

    while (normalized.length > 0 && normalized[normalized.length - 1].length === 0) {
        normalized.pop();
    }

    return normalized;
}

function normalizeArmoryRecipeForIdentity(recipe: SharedArmoryRecipe): SharedArmoryRecipe | undefined {
    const normalized = normalizeArmoryRecipe(recipe);
    if (!normalized) {
        return undefined;
    }

    return {
        bundledDatasetVersion: normalized.bundledDatasetVersion,
        ...(normalized.argonaut ? { argonaut: sanitizeSharedArgonaut(normalized.argonaut) } : {}),
        ...(normalized.titan ? { titan: normalizeArmorySelectionForIdentity(normalized.titan) } : {}),
        ...(normalized.kratosTable ? { kratosTable: normalizeArmorySelectionForIdentity(normalized.kratosTable) } : {}),
        ...(normalized.weapon ? { weapon: normalizeArmorySelectionForIdentity(normalized.weapon) } : {}),
        ...(normalized.gear ? { gear: normalized.gear.map((selection) => normalizeArmorySelectionForIdentity(selection)) } : {}),
        ...(normalized.mnemos ? { mnemos: normalized.mnemos.map((selection) => normalizeArmorySelectionForIdentity(selection)) } : {}),
    };
}

function normalizeArmorySelectionForIdentity(selection: SharedArmoryItemSelection): SharedArmoryItemSelection {
    if (selection.kind === 'bundled-ref') {
        return {
            kind: 'bundled-ref',
            id: selection.id,
        };
    }

    return {
        kind: 'custom-item',
        item: sanitizeCustomArmoryItem(selection.item),
    };
}

function normalizeEnvelope(value: unknown): SharedLoadoutEnvelope | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const envelope = value as Partial<SharedLoadoutEnvelope>;
    if (envelope.version !== LOADOUT_SHARE_ENVELOPE_VERSION || envelope.scope !== 'loadout' || envelope.importTarget !== 'manual') {
        return null;
    }

    if (envelope.inputSource !== 'manual' && envelope.inputSource !== 'armory') {
        return null;
    }

    const rawPayload = envelope.payload;
    if (!rawPayload || typeof rawPayload !== 'object' || !('snapshot' in rawPayload)) {
        return null;
    }

    const snapshot = sanitizeSnapshot((rawPayload as { snapshot: LoadoutStats }).snapshot);
    const armoryRecipe = normalizeArmoryRecipe((rawPayload as { armoryRecipe?: SharedArmoryRecipe }).armoryRecipe);
    const meta = normalizeMeta(envelope.meta, snapshot.name);

    return {
        version: LOADOUT_SHARE_ENVELOPE_VERSION,
        scope: 'loadout',
        importTarget: 'manual',
        inputSource: envelope.inputSource,
        payload: {
            snapshot,
            ...(armoryRecipe ? { armoryRecipe } : {}),
        },
        ...(meta ? { meta } : {}),
    };
}

function normalizeMeta(meta: SharedLoadoutMeta | undefined, fallbackTitle: string): SharedLoadoutMeta | undefined {
    if (!meta || typeof meta !== 'object') {
        return fallbackTitle.trim()
            ? {
                  sourceLoadoutName: fallbackTitle.trim(),
              }
            : undefined;
    }

    const sourceLoadoutName = meta.sourceLoadoutName?.trim() || fallbackTitle.trim() || undefined;
    const sharedAt = meta.sharedAt?.trim() || undefined;

    if (!sourceLoadoutName && !sharedAt) {
        return undefined;
    }

    return {
        ...(sharedAt ? { sharedAt } : {}),
        ...(sourceLoadoutName ? { sourceLoadoutName } : {}),
    };
}

function normalizeArmoryRecipe(recipe: SharedArmoryRecipe | undefined): SharedArmoryRecipe | undefined {
    if (!recipe || typeof recipe !== 'object') {
        return undefined;
    }

    const argonaut = recipe.argonaut ? sanitizeSharedArgonaut(recipe.argonaut) : undefined;
    const titan = normalizeArmorySelection(recipe.titan);
    const kratosTable = normalizeArmorySelection(recipe.kratosTable);
    const weapon = normalizeArmorySelection(recipe.weapon);
    const gear = normalizeArmorySelectionList(recipe.gear);
    const mnemos = normalizeArmorySelectionList(recipe.mnemos);
    const bundledDatasetVersion = recipe.bundledDatasetVersion?.trim() || BUNDLED_ARMORY_SHARE_CONTRACT_VERSION;

    if (!argonaut && !titan && !kratosTable && !weapon && !gear && !mnemos) {
        return undefined;
    }

    return {
        bundledDatasetVersion,
        ...(argonaut ? { argonaut } : {}),
        ...(titan ? { titan } : {}),
        ...(kratosTable ? { kratosTable } : {}),
        ...(weapon ? { weapon } : {}),
        ...(gear ? { gear } : {}),
        ...(mnemos ? { mnemos } : {}),
    };
}

function normalizeArmorySelection(selection: SharedArmoryItemSelection | undefined): SharedArmoryItemSelection | undefined {
    if (!selection || typeof selection !== 'object') {
        return undefined;
    }

    if (selection.kind === 'bundled-ref') {
        const id = selection.id?.trim();
        const name = selection.name?.trim() || undefined;
        if (!id) {
            return undefined;
        }

        return {
            kind: 'bundled-ref',
            id,
            ...(name ? { name } : {}),
        };
    }

    if (selection.kind === 'custom-item') {
        try {
            return {
                kind: 'custom-item',
                item: sanitizeCustomArmoryItem(selection.item),
            };
        } catch {
            return undefined;
        }
    }

    return undefined;
}

function normalizeArmorySelectionList(
    selections: SharedArmoryItemSelection[] | undefined,
): SharedArmoryItemSelection[] | undefined {
    if (!Array.isArray(selections) || selections.length === 0) {
        return undefined;
    }

    const normalized = selections.map((selection) => normalizeArmorySelection(selection)).filter(Boolean) as SharedArmoryItemSelection[];
    return normalized.length > 0 ? normalized : undefined;
}

function sanitizeSnapshot(loadout: LoadoutStats): LoadoutStats {
    return sanitizeLoadoutStats(deepCopy(loadout));
}

function sanitizeCustomArmoryItem(item: ArmoryItem): ArmoryItem {
    const name = item?.name?.trim();
    if (!name) {
        throw new Error('Shared custom armory item is missing a name.');
    }

    return {
        ...deepCopy(item),
        name,
        tags: deepCopy(item.tags ?? []),
        id: item.id?.trim() || undefined,
        source: 'custom',
        bundledOriginId: item.bundledOriginId?.trim() || undefined,
        bundledOriginName: item.bundledOriginName?.trim() || undefined,
        stats: (item.stats ?? []).map((stats) => sanitizeLoadoutStatsWithRequirements(deepCopy(stats))),
    };
}

function sanitizeSharedArgonaut(argonaut: Argonaut): Argonaut {
    const sanitizedName = argonaut?.name?.trim() || 'Argonaut';
    const source = sanitizeLoadoutStats(deepCopy(argonaut));

    return {
        ...source,
        name: sanitizedName,
        rage: Math.max(0, Math.min(9, argonaut?.rage ?? 0)),
        fate: Math.max(0, Math.min(9, argonaut?.fate ?? 0)),
        danger: Math.max(0, Math.min(9, argonaut?.danger ?? 0)),
        ambrosia: Math.max(0, argonaut?.ambrosia ?? 0),
        despair: Math.max(0, argonaut?.despair ?? 0),
        bleed: Math.max(0, argonaut?.bleed ?? 0),
        midas: Math.max(0, argonaut?.midas ?? 0),
        pain: Math.max(0, argonaut?.pain ?? 0),
    } as Argonaut;
}

function encodeBase64Url(value: string): string {
    const binary = encodeURIComponent(value).replace(/%([0-9A-F]{2})/gu, (_match, code) => {
        return String.fromCharCode(parseInt(code, 16));
    });

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

function decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const paddingLength = (4 - (normalized.length % 4 || 4)) % 4;
    const padded = `${normalized}${'='.repeat(paddingLength)}`;
    const binary = atob(padded);
    const percentEncoded = Array.from(binary)
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('');
    return decodeURIComponent(percentEncoded);
}
